import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { BellPlus, CheckCircle2, Search } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const post = async (path, body) => {
  const r = await fetch(`${API}${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Request failed");
  return d;
};

export function ReminderWorkspace({ token, center, onChanged }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ open: [], found: [], today: "" });
  const [sel, setSel] = useState(null);
  const [f, setF] = useState({ title: "", due: "", assigned: "", notes: "" });
  const load = useCallback(() => post("/api/booking-workspace/followups", { token, center, search: q }).then(setData).catch((e) => toast.error(e.message)), [token, center, q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  const save = async () => {
    if (!sel || !f.title.trim() || !f.due) return toast.error("Pick the booking, add a title and due date");
    if (sel.follow_up_required && !window.confirm("This booking already has a reminder. Replace it?")) return;
    const remark = [f.title.trim(), f.assigned.trim() && `Assigned: ${f.assigned.trim()}`, f.notes.trim()].filter(Boolean).join(" · ");
    try {
      await post(`/api/bookings/update/${sel.booking_id}`, { token, follow_up_required: true, follow_up_date: f.due, follow_up_remark: remark });
      toast.success("Reminder saved"); setSel(null); setF({ title: "", due: "", assigned: "", notes: "" }); setQ(""); load(); onChanged?.();
    } catch (e) { toast.error(e.message); }
  };
  const done = async (b) => {
    try { await post(`/api/bookings/update/${b.booking_id}`, { token, follow_up_required: false }); toast.success("Marked complete"); load(); onChanged?.(); }
    catch (e) { toast.error(e.message); }
  };
  const due = (b) => (b.follow_up_date < data.today ? ["Overdue", "bg-red-100 text-red-800"] : b.follow_up_date === data.today ? ["Due today", "bg-amber-100 text-amber-800"] : ["Upcoming", "bg-sky-100 text-sky-800"]);
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]" data-testid="bi-reminders-ws">
      <div className="bi-panel">
        <h2 className="bi-panel-title"><BellPlus className="h-5 w-5" />New Reminder</h2>
        <div className="space-y-3">
          <div><Label>Related booking *</Label>
            {sel ? (
              <div className="flex items-center justify-between rounded-xl bg-white p-3 text-base"><span><b>{sel.guest_name}</b> · {sel.date} {sel.time_slot} · {sel.center}</span><Button variant="ghost" onClick={() => setSel(null)}>Change</Button></div>
            ) : (<>
              <div className="relative"><Search className="absolute left-3 top-3.5 h-5 w-5 text-[#a8781f]" /><Input className="h-12 pl-10" placeholder="Search guest name, phone or booking ID" value={q} onChange={(e) => setQ(e.target.value)} data-testid="rem-search" /></div>
              {data.found.map((b) => <button key={b.booking_id} type="button" className="mt-1 w-full rounded-lg bg-white p-2 text-left hover:bg-[#fff6df]" onClick={() => setSel(b)} data-testid={`rem-pick-${b.booking_id}`}>{b.guest_name} · {b.phone} · {b.date}</button>)}
            </>)}
          </div>
          <div><Label>Reminder title *</Label><Input className="h-12" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Call to confirm menu" data-testid="rem-title" /></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Due date *</Label><Input type="date" className="h-12" value={f.due} onChange={(e) => setF({ ...f, due: e.target.value })} data-testid="rem-due" /></div>
            <div><Label>Assigned person</Label><Input className="h-12" value={f.assigned} onChange={(e) => setF({ ...f, assigned: e.target.value })} data-testid="rem-assigned" /></div>
          </div>
          <div><Label>Notes (optional)</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="rem-notes" /></div>
          <Button className="h-12 w-full bg-[#8a6a1f] text-base hover:bg-[#6d5318]" onClick={save} data-testid="rem-save">Save Reminder</Button>
        </div>
      </div>
      <div className="bi-panel">
        <h2 className="bi-panel-title">Open reminders</h2>
        {!data.open.length ? <p className="text-base text-muted-foreground">No open reminders.</p> : data.open.map((b) => {
          const [lbl, cls] = due(b);
          return (
            <div key={b.booking_id} className="mb-2 flex flex-wrap items-center gap-3 rounded-xl bg-white p-3 text-base shadow-sm" data-testid={`rem-row-${b.booking_id}`}>
              <Badge className={cls}>{lbl}</Badge><span className="font-semibold">{b.follow_up_date}</span>
              <span className="flex-1">{b.follow_up_remark || "Follow up"} <span className="text-muted-foreground">— {b.guest_name} · {b.phone}</span></span>
              <Button variant="outline" className="h-11" onClick={() => done(b)} data-testid={`rem-done-${b.booking_id}`}><CheckCircle2 className="mr-1 h-4 w-4" />Done</Button>
            </div>);
        })}
      </div>
    </div>
  );
}
